import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5050/api',
});

// Attach JWT token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('forgest_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const getDashboard = () => api.get('/dashboard');
export const getRendements = (annee: number) => api.get(`/dashboard/rendements/${annee}`);
export const getPartsCategories = () => api.get('/dashboard/parts-categories');
export const getPartenairesStats = () => api.get('/dashboard/partenaires-stats');

export const getPartenaires = () => api.get('/partenaires');
export const getPartenaire = (id: string) => api.get(`/partenaires/${id}`);

export const getForages = (params?: Record<string, string>) => api.get('/forages', { params });
export const getForagesCartographie = (params?: Record<string, string>) => api.get('/forages/cartographie', { params });
export const getAbonnes = (params?: Record<string, string>) => api.get('/abonnes', { params });
export const getFactures = (params?: Record<string, string>) => api.get('/factures', { params });
export const getPaiements = (params?: Record<string, string>) => api.get('/paiements', { params });
export const getPaiementStats = (params?: Record<string, string>) => api.get('/paiements/stats', { params });
export const getSecteurs = (params?: Record<string, string>) => api.get('/secteurs', { params });
export const getLocalites = (params?: Record<string, string>) => api.get('/localites', { params });
export const getProductions = (params?: Record<string, string>) => api.get('/productions', { params });
export const getCharges = (params?: Record<string, string>) => api.get('/charges', { params });
export const getStatistiques = (params?: Record<string, string>) => api.get('/statistiques', { params });

// Users
export const getUsers = (params?: Record<string, string>) => api.get('/users', { params });
export const getUserStats = () => api.get('/users/stats');
export const createUser = (data: any) => api.post('/users', data);
export const updateUser = (id: string, data: any) => api.put(`/users/${id}`, data);
export const toggleUserStatus = (id: string, data?: any) => api.put(`/users/${id}/toggle-status`, data);
export const resetUserPassword = (id: string, data?: any) => api.put(`/users/${id}/reset-password`, data);
export const getUserAffectations = (id: string) => api.get(`/users/${id}/affectations`);
export const setUserAffectations = (id: string, data: { secteur_ids: string[]; localite_ids: string[] }) => api.put(`/users/${id}/affectations`, data);
export const clearUserAffectations = (id: string) => api.delete(`/users/${id}/affectations`);

// Comptabilite
export const getComptaResume = (params?: Record<string, string>) => api.get('/comptabilite/resume', { params });
export const getComptaMensuel = (params?: Record<string, string>) => api.get('/comptabilite/mensuel', { params });
export const getComptaParPartenaire = (params?: Record<string, string>) => api.get('/comptabilite/par-partenaire', { params });
export const getComptaChargesParType = (params?: Record<string, string>) => api.get('/comptabilite/charges-par-type', { params });
export const getComptaPaiementsParType = (params?: Record<string, string>) => api.get('/comptabilite/paiements-par-type', { params });

// Auth
export const login = (email: string, password: string) => api.post('/auth/login', { email, password });
export const requestOtp = (email: string) => api.post('/auth/login-otp', { email });
export const verifyOtp = (email: string, otp_code: string) => api.post('/auth/verify-otp', { email, otp_code });
export const getMe = () => api.get('/auth/me');

// Mobile Payments
export const getMobilePayments = (params?: Record<string, string>) => api.get('/mobile-payments', { params });
export const getMobilePaymentStats = (params?: Record<string, string>) => api.get('/mobile-payments/stats', { params });
export const createMobilePayment = (data: any) => api.post('/mobile-payments', data);
export const syncMobilePayments = () => api.post('/mobile-payments/sync');

// SMS Broadcast
export const getSmsBroadcastAbonnes = (params?: Record<string, string>) => api.get('/sms-broadcast/abonnes', { params });
export const sendSmsBroadcast = (data: {
  message: string;
  abonne_ids: number[];
  personalisation: boolean;
  channel?: 'sms' | 'whatsapp';
  custom_recipients?: { nom: string; telephone: string }[];
}) => api.post('/sms-broadcast/send', data);
export const getSmsBroadcastHistory = () => api.get('/sms-broadcast/history');

// Agent Location tracking
export const generateAgentLocationLink = (user_id: string) => api.post('/agent-location/generate-link', { user_id });
export const getAgentLocationsLatest = (hours?: number) => api.get('/agent-location/latest', { params: hours ? { hours } : undefined });
export const getAgentLocationHistory = (user_id: string, limit?: number) => api.get(`/agent-location/history/${user_id}`, { params: limit ? { limit } : undefined });

// WhatsApp
export const getWhatsappStatus = () => api.get('/whatsapp/status');
export const initWhatsapp = () => api.post('/whatsapp/init');
export const logoutWhatsapp = () => api.post('/whatsapp/logout');
export const testWhatsappSend = (data: { phone: string; message: string }) => api.post('/whatsapp/test-send', data);

// Tracking / Audit
export const getTrackingStats = (params?: Record<string, string>) => api.get('/tracking/stats', { params });
export const getTrackingLogs = (params?: Record<string, string>) => api.get('/tracking/logs', { params });
export const getTrackingUsers = (params?: Record<string, string>) => api.get('/tracking/users', { params });
export const getTrackingUser = (userId: string, params?: Record<string, string>) => api.get(`/tracking/user/${userId}`, { params });
export const getTrackingSessions = (params?: Record<string, string>) => api.get('/tracking/sessions', { params });

// Analytics
export const getAnalyticsKpi = (params?: Record<string, string>) => api.get('/analytics/kpi', { params });
export const getAnalyticsForecast = (params?: Record<string, string>) => api.get('/analytics/forecast/revenue', { params });
export const getAnalyticsRisk = (params?: Record<string, string>) => api.get('/analytics/risk/payment', { params });
export const getAnalyticsConsumption = (params?: Record<string, string>) => api.get('/analytics/consumption/patterns', { params });
export const getAnalyticsTopFlop = (params?: Record<string, string>) => api.get('/analytics/topflop', { params });
export const getAnalyticsEfficiency = (params?: Record<string, string>) => api.get('/analytics/efficiency', { params });
export const getAnalyticsExpenses = (params?: Record<string, string>) => api.get('/analytics/expenses', { params });
export const getAnalyticsGeographic = (params?: Record<string, string>) => api.get('/analytics/geographic', { params });
export const getAnalyticsRecommendations = (params?: Record<string, string>) => api.get('/analytics/recommendations', { params });

export default api;
